import type {
  LocaleBundle,
  NotPriceableReason,
  Alternative,
  ParseResponse,
  PlaceBetResponse,
  Presentation,
  Slip,
  StakeContext,
  PlayerAccount,
} from '@geni/contracts';
import type { SportKind } from '@geni/contracts/sport';
import type { OddsFormat } from '@geni/contracts/odds-format';
import type { Teaser } from './teaser.js';

/**
 * What the widget knows, and how it changes.
 *
 * A reducer rather than fields mutated in event handlers, because the awkward
 * states are the ones worth being able to write down: a reply that arrived
 * after the bettor typed again, a confirm that came back PRICE_MOVED, a
 * jurisdiction where there is nothing to type into. Each is a value here and a
 * test there.
 *
 * Nothing in this file touches the DOM or the network. That is what makes the
 * whole widget testable without a browser.
 */

export type WidgetPhase =
  /** Collapsed launcher. Nothing fetched yet. */
  | 'CLOSED'
  /** Opening a session and fetching strings. */
  | 'OPENING'
  /** Waiting for the bettor. */
  | 'READY'
  /** A parse is in flight. */
  | 'THINKING'
  /** A confirm is in flight. */
  | 'CONFIRMING'
  /** A ticket was issued and handed to the operator's slip. */
  | 'HANDED_OFF'
  /**
   * A confirmed bet is with the operator's betting engine.
   *
   * Distinct from CONFIRMING because the two are different promises. CONFIRMING
   * is us re-pricing and signing; this is somebody's money moving on the
   * operator's ledger, and it is the one state in the widget where a retry is
   * not obviously safe.
   */
  | 'PLACING'
  /** The operator placed it. Terminal for this bet. */
  | 'PLACED'
  /** Could not start at all. */
  | 'FAILED';

export interface WidgetMessage {
  from: 'player' | 'assistant';
  text: string;
  /**
   * A pre-formatted clock time, or absent.
   *
   * A string rather than a Date because formatting it is a locale decision and
   * this file must stay pure — a reducer that reads the clock is one whose
   * output depends on when the test ran. Whoever dispatches the event supplies
   * it, and nothing breaks when nobody does.
   */
  at?: string;
}

/** Why something went wrong, in terms the string tables have words for. */
export type WidgetErrorKind = 'GENERIC' | 'RATE_LIMITED' | 'SESSION' | 'BOARD' | 'SPENT';

/**
 * What the collapsed pill calls itself when the operator has not renamed it.
 *
 * A proper noun, so showing it to somebody who did not ask for English is not
 * showing them English — and it is the same before and after the string tables
 * arrive, which is the whole point of it living here.
 */
export const DEFAULT_BRAND = 'Geni AI';

export interface WidgetState {
  phase: WidgetPhase;
  /** Null until the bundle arrives; every string comes from it. */
  bundle: LocaleBundle | null;
  messages: WidgetMessage[];
  slips: Slip[];
  /**
   * What else is quoted on this slip's fixtures, as things to press.
   *
   * Keyed by slip id, and already trimmed by the server — see `mostPopular`.
   *
   * A map rather than one list because an answer may offer several slips, and
   * the chips belong to whichever the bettor ends up looking at. Holding them
   * all means a choice made in the browser needs no second request: the entry
   * for the chosen slip is already here.
   *
   * Missing for a slip with nothing sensible to build on — an operator whose
   * platform will not price two selections from one match, or a fixture where
   * everything else quoted answers a question the slip already answers.
   */
  alternatives: Record<string, Alternative[]>;
  /**
   * Whether the answer left out a selection another one already settles.
   *
   * Kept beside `presentation` and cleared with it, because it describes the
   * same answer: what the gate did to this request and nothing later.
   */
  mergedLegs: boolean;
  /** Whether the quick-options panel is open. Closed until asked for. */
  buildOpen: boolean;
  /**
   * Whether "add another match" has become the field it offers.
   *
   * Collapsed, the row is one button that names what it is for. Pressed, the
   * button *is* the box you type into. It used to be both at once — a field,
   * and under it a button whose only act was to move the cursor into the field
   * above it — which read as two ways to do one thing, and made the press look
   * like it had done nothing.
   */
  buildTyping: boolean;
  /**
   * Which of them are ticked, waiting to be confirmed together.
   *
   * Ticking is free and local; only the confirm reaches the network. That is
   * what lets somebody change their mind three times without repricing three
   * times, and without watching a total jump under them.
   */
  buildPicked: string[];
  /**
   * Set when a confirm came back PRICE_MOVED.
   *
   * Kept as the previous price rather than a boolean, because the bettor is
   * being asked to agree to a change and "it moved" without saying from what
   * is not a thing anyone can agree to.
   */
  movedFrom: number | null;
  /**
   * How the last answer should be shown.
   *
   * The API has always sent this and the widget used to throw it away, which is
   * why a target-odds search and a matched bet looked identical, and why
   * "understood, but nothing matched" rendered as silence. It maps one-to-one
   * onto what actually happened, so nothing here has to infer intent from the
   * shape of the slips.
   */
  presentation: Presentation | null;
  /** Why a combined price was not available. Only set alongside the above. */
  notPriceable: NotPriceableReason | null;

  /**
   * The id of the answer currently on screen.
   *
   * Kept for one purpose: it is what a verdict is *about*. Without it a tap
   * says "something was wrong" and joins to nothing; with it, the record on
   * the server already holds what was asked, what the model was shown, what it
   * answered before the gate and what the gate did — which is the difference
   * between a complaint and a case file.
   */
  answerId: string | null;

  /**
   * What the bettor has said about that answer, if anything.
   *
   * Three states rather than two, because "asked why" is a real one: the
   * approving tap finishes immediately, and the disapproving one opens three
   * reasons and waits. `null` is the ordinary case — most answers are never
   * rated, and that has to look like nothing on the screen.
   */
  verdict: 'HELPFUL' | 'NOT_HELPFUL' | 'ASKED_WHY' | null;

  /** Whether the free-text form is open. Reached from the panel, not an answer. */
  feedbackFormOpen: boolean;

  /**
   * Whether the full responsible-gambling wording is expanded.
   *
   * Only the *full* wording. A short line stays on screen either way, because
   * `requireRgMessage` says the message must be shown rather than reachable,
   * and a footer that hid all of it behind a press would be answering a
   * regulator's question with "it is one tap away".
   */
  rgOpen: boolean;

  /**
   * The stake conversation, as the server last described it.
   *
   * Null means there is no amount to name: no bet, several bets and no way to
   * know which, or a jurisdiction where a wager is not collected here at all.
   * In every one of those the widget behaves exactly as it did before in-chat
   * betting existed, which is what makes the feature additive rather than a
   * rewrite of the confirm path.
   */
  stake: StakeContext | null;
  /**
   * The bettor's account, fetched from the operator's own page.
   *
   * Deliberately not from ours. `StakeContext.account` exists and our server
   * can fill it, but our server does not know who the bettor is: a session is
   * minted from a publishable key and carries no identity. Filling it would
   * mean the operator naming their player to us, and then we would be holding
   * balances — a thing to defend, disclose, and lose.
   *
   * So the number comes the same way a placed bet goes: a request from this
   * browser to the operator's own endpoint, same-origin, carrying their cookie.
   * They know who is asking. We never learn it, and could not say it if asked.
   *
   * Null while unfetched and null when the operator configured no endpoint.
   * Both mean the same thing downstream — no account, so no affordability
   * check — which is the behaviour that was there before this existed.
   */
  account: PlayerAccount | null;

  /**
   * What the bettor has since typed or tapped.
   *
   * Held separately from `stake.amount` on purpose. `stake` is the server's
   * account of the last turn; this is the bettor changing their mind between
   * turns, and it must not need a round trip — tapping a suggested amount
   * should update the card immediately, and it does, because `assessStake` is
   * the same function on both sides.
   */
  stakeAmount: number | null;

  /**
   * How the operator answered when the bet was handed to them.
   *
   * Kept as their own reason rather than as a boolean: "there wasn't enough in
   * the account" and "that market closed" call for completely different next
   * moves from the bettor, and collapsing them into "failed" would leave them
   * guessing which.
   */
  placement: PlaceBetResponse | null;

  /**
   * What page of the sportsbook the bettor is on.
   *
   * Configuration rather than conversation, which is why it survives a close
   * and a new question: the bettor moving from football to CS:GO is the
   * operator's page telling us so, not something the assistant worked out.
   *
   * `prompts` are the operator's own, naming matches on the board the visitor
   * is looking at. Empty falls back to one written prompt per sport from the
   * string tables — see the note on `widget.prompts.*`. Only the operator knows
   * what it is trading, so only the operator can name a fixture here.
   */
  context: {
    sport: SportKind | null;
    prompts: readonly string[];
    /**
     * How the page shows prices, so the panel shows them the same way.
     *
     * Page context like the sport, and held here for the same reason: the
     * bettor chose it on the operator's page, and a close does not unchoose
     * it. Decimal unless the page says otherwise, which is what every panel
     * showed before this existed.
     */
    oddsFormat: OddsFormat;
  };

  /**
   * What the collapsed launcher's mini-stage is doing right now.
   *
   * Null draws the mark, which is what an operator who declares no sport gets
   * and what the widget did before this existed. Held in state rather than in
   * the element so that "which action is playing" is a value a test can assert
   * — and so a repaint, which is what restarts the CSS animation, is the same
   * mechanism as every other change on screen.
   */
  teaser: Teaser | null;

  /**
   * What the closed pill is called.
   *
   * Configuration, like the sport, and held in state for the same reason: it
   * survives a close, and both render paths read it rather than one reading an
   * attribute and the other a string table.
   *
   * It is deliberately NOT `widget.launcher.label`. That string is "Describe a
   * bet" — a call to action, written when the launcher was a circle that
   * revealed it on hover. The pill shows its label permanently now, so reading
   * the tables there made the button announce itself as the brand until the
   * bundle arrived and as an instruction ever after: "Geni AI" before the
   * first open, "Describe una apuesta" after the first close. The design moved
   * and the string did not.
   */
  brand: string;

  error: WidgetErrorKind | null;
}

export const initialState: WidgetState = {
  phase: 'CLOSED',
  bundle: null,
  messages: [],
  slips: [],
  alternatives: {},
  mergedLegs: false,
  buildOpen: false,
  buildTyping: false,
  buildPicked: [],
  movedFrom: null,
  presentation: null,
  notPriceable: null,
  answerId: null,
  verdict: null,
  feedbackFormOpen: false,
  rgOpen: false,
  stake: null,
  account: null,
  stakeAmount: null,
  placement: null,
  context: { sport: null, prompts: [], oddsFormat: 'decimal' },
  teaser: null,
  brand: DEFAULT_BRAND,
  error: null,
};

export type WidgetEvent =
  | { type: 'OPEN' }
  | { type: 'OPENED'; bundle: LocaleBundle }
  | {
      type: 'OPEN_FAILED';
      kind?: WidgetErrorKind;
      /**
       * The strings, when they arrived before the refusal did.
       *
       * Without them the panel has no language and paints nothing at all, so
       * a refused session showed as a launcher that did nothing when pressed.
       * They are fetched first precisely so this can be filled in.
       */
      bundle?: LocaleBundle;
    }
  | { type: 'CLOSE' }
  | {
      type: 'ASK';
      text: string;
      at?: string;
      /**
       * Whether this question is adding to the slip on screen. See ANSWERED.
       *
       * It keeps the bet visible while the addition is being fetched. Without
       * it the slip vanishes the moment Enter is pressed and comes back a
       * second later — or, if nothing was found, does not come back at all.
       */
      growing?: boolean;
    }
  | {
      type: 'ANSWERED';
      response: ParseResponse;
      /**
       * Whether the request was adding to the slip on screen.
       *
       * Set only by the field under a slip. It changes exactly one thing: an
       * answer that found nothing leaves the slip where it is. Growing a bet
       * and losing it is the worst outcome the row can produce, and it is the
       * one it promises not to — the bettor asked for one more leg and would
       * be handed an empty panel and a sentence about a market.
       *
       * Only the empty case. An answer that *did* find something replaces the
       * slip as usual, because that answer already contains the legs it was
       * growing — the server put them there.
       */
      growing?: boolean;
    }
  /**
   * The bettor said what they thought of the answer on screen.
   *
   * Applied immediately and never awaits the network. The tap is an opinion,
   * not a transaction: making somebody watch a spinner to find out whether
   * their opinion saved would cost more than the opinion is worth, and a
   * failure to store one is not something they can act on.
   */
  | { type: 'VERDICT'; verdict: 'HELPFUL' | 'NOT_HELPFUL' }
  /** A reason chosen from the three offered after a disapproving tap. */
  | { type: 'VERDICT_REASON' }
  | { type: 'FEEDBACK_FORM'; open: boolean }
  | { type: 'RG_PANEL'; open: boolean }
  /**
   * One of several suggestions picked, before any amount is named.
   *
   * Not a confirm and not a network call. Where a price search answered with
   * three accas, "how much?" had nothing to attach to, so the stake step stayed
   * away and the only button on each card handed the bettor to the operator's
   * own slip — which is the older road, taken by somebody who had just made the
   * exact choice that road exists to collect. This narrows the answer to the
   * one they picked and lets the amount be asked for here.
   */
  | { type: 'SLIP_CHOSEN'; slipId: string; label?: string; at?: string }
  /** One more selection priced onto the slip already on screen. */
  | { type: 'SLIP_EXTENDED'; slip: Slip; alternatives: Alternative[] }
  /** One leg taken back off it, and the rest repriced. */
  | { type: 'SLIP_REDUCED'; slip: Slip; alternatives: Alternative[] }
  /** The quick-options panel opened or closed. */
  | { type: 'BUILD_TOGGLED' }
  | { type: 'BUILD_TYPING'; open: boolean }
  /** One option ticked or unticked. Local until confirmed. */
  | { type: 'BUILD_PICKED'; ref: string }
  | { type: 'CONFIRM' }
  | { type: 'CONFIRMED' }
  | { type: 'PRICE_MOVED'; slip: Slip; previousTotalPrice: number }
  /** A suggested amount tapped, or one typed into the field. */
  | { type: 'STAKE_CHOSEN'; amount: number | null }
  /** The bet is with the operator's engine. */
  | { type: 'ACCOUNT'; account: PlayerAccount | null }
  | { type: 'PLACING' }
  /** They answered — placed or refused. Both arrive here. */
  | { type: 'PLACEMENT'; result: PlaceBetResponse }
  /**
   * The host page declaring what it is: which sport the visitor is looking at,
   * what is worth asking about, and what to call the assistant.
   */
  | {
      type: 'CONTEXT';
      sport: SportKind | null;
      prompts: readonly string[];
      brand?: string;
      oddsFormat?: OddsFormat;
    }
  /** The launcher's stage moved on to its next action. */
  | { type: 'TEASER'; teaser: Teaser | null }
  | { type: 'FAILED'; kind: WidgetErrorKind };

export function reduce(state: WidgetState, event: WidgetEvent): WidgetState {
  switch (event.type) {
    case 'OPEN':
      return { ...state, phase: 'OPENING', error: null };

    case 'OPENED':
      return { ...state, phase: 'READY', bundle: event.bundle, error: null };

    case 'OPEN_FAILED':
      /**
       * Why it failed, not just that it did.
       *
       * The kind used to be dropped here and every refused session read as
       * "something went wrong" — including a demo link that had simply spent
       * its budget, which is not a fault and cannot be fixed by retrying.
       */
      return {
        ...state,
        phase: 'FAILED',
        error: event.kind ?? 'GENERIC',
        bundle: event.bundle ?? state.bundle,
      };

    case 'CLOSE':
      // The bundle survives a close. Reopening should not refetch strings that
      // have not changed, and it should certainly not show a bettor an empty
      // panel while it does. The context survives for the same reason and a
      // stronger one: it describes the page, which has not changed just
      // because somebody shut the panel.
      return {
        ...initialState,
        bundle: state.bundle,
        context: state.context,
        // The stage carries on where it left off rather than restarting, so
        // closing the panel does not replay an action the bettor just watched.
        teaser: state.teaser,
        // The pill is called what it was called before it was opened. This is
        // the bug that made it read "Describe una apuesta" on the way back.
        brand: state.brand,
      };

    case 'CONTEXT':
      return {
        ...state,
        context: {
          sport: event.sport,
          prompts: event.prompts,
          oddsFormat: event.oddsFormat ?? 'decimal',
        },
        // An operator who clears the attribute goes back to the product's own
        // name rather than to an empty pill.
        brand: event.brand === undefined || event.brand.trim() === '' ? DEFAULT_BRAND : event.brand,
      };

    case 'TEASER':
      return { ...state, teaser: event.teaser };

    case 'ASK':
      return {
        ...state,
        phase: 'THINKING',
        messages: [
          ...state.messages,
          { from: 'player', text: event.text, ...(event.at === undefined ? {} : { at: event.at }) },
        ],
        /**
         * The previous answer's slips go the moment a new question is asked.
         *
         * Leaving them on screen invites confirming a bet that belonged to a
         * question the bettor has moved on from. The presentation goes with
         * them, or a "nothing matched" notice would outlive the question it
         * answered.
         *
         * Unless the question is adding to that very slip, which is the one
         * case where the bettor has *not* moved on. Then it stays — through the
         * wait and, if nothing was found, after it. See `growing`.
         */
        ...(event.growing === true
          ? {}
          : {
              slips: [],
              alternatives: {},
              presentation: null,
              notPriceable: null,
              mergedLegs: false,
              // The amount goes with the question it belonged to. Carrying
              // "50" over to whatever they ask next is how somebody ends up
              // staking a figure they named for a different bet.
              stake: null,
              stakeAmount: null,
            }),
        // Closed either way: whatever was ticked has been asked for, or was
        // never part of this question.
        buildOpen: false,
        buildTyping: false,
        buildPicked: [],
        movedFrom: null,
        placement: null,
        error: null,
      };

    case 'ANSWERED':
      return {
        ...state,
        phase: 'READY',
        messages: [...state.messages, { from: 'assistant', text: event.response.reply }],
        // Kept when a request that was growing a slip came back with none. See
        // `growing`, and note the chips below are kept with it: they belong to
        // the slip on screen, and that slip has not changed.
        slips:
          event.growing === true && event.response.slips.length === 0
            ? state.slips
            : event.response.slips,
        // `?? []` for the same reason the stake is guarded: an older API, or a
        // proxy that dropped a key, must lose the chips rather than the panel.
        alternatives:
          event.growing === true && event.response.slips.length === 0
            ? state.alternatives
            : (event.response.alternatives ?? {}),
        movedFrom: null,
        presentation: event.response.presentation,
        // `?? false` for the same reason as the fields around it: an older API
        // must lose the line, not the panel.
        mergedLegs: event.response.mergedLegs ?? false,
        notPriceable: event.response.reason,
        // `?? null` rather than trusting the field to be there. This response
        // is JSON off a network, and an older API — or a proxy that dropped a
        // key — must degrade to "no stake conversation" rather than to a
        // widget that throws while painting.
        //
        // Kept alongside the slip when nothing was added: the amount belongs to
        // the bet on screen, and that bet is the one they already had. Clearing
        // it would leave a slip with no way to stake it.
        stake:
          event.growing === true && event.response.slips.length === 0
            ? state.stake
            : (event.response.stake ?? null),
        // Seeded from what the bettor said in the message just answered, which
        // is null whenever they did not name an amount — the case that makes
        // the assistant ask instead of assume.
        stakeAmount:
          event.growing === true && event.response.slips.length === 0
            ? state.stakeAmount
            : (event.response.stake?.amount ?? null),
        placement: null,
        answerId: event.response.requestId,
        // A verdict belongs to the answer it was given about. A new answer
        // clears it, or the thumbs would arrive already pressed.
        verdict: null,
      };

    case 'VERDICT':
      /**
       * Approval finishes here; disapproval asks one question.
       *
       * The asymmetry is the whole design of this control. Nobody introspects
       * on success, so a follow-up after a good answer would cost most of the
       * taps and return sentences saying "it was fine". After a bad one the
       * three reasons are worth a screen of their own, because each points at
       * a different place in the pipeline.
       */
      return {
        ...state,
        verdict: event.verdict === 'HELPFUL' ? 'HELPFUL' : 'ASKED_WHY',
      };

    case 'VERDICT_REASON':
      return { ...state, verdict: 'NOT_HELPFUL' };

    case 'FEEDBACK_FORM':
      return { ...state, feedbackFormOpen: event.open };

    case 'RG_PANEL':
      return { ...state, rgOpen: event.open };

    case 'STAKE_CHOSEN':
      // Deliberately does not re-ask the server. `assessStake` is shared with
      // the route, so the card, the warning and the quick actions all move on
      // the same arithmetic the server would have applied — without a round
      // trip, and without a model call for a number the bettor just typed.
      return { ...state, stakeAmount: event.amount, placement: null, error: null };

    /* What the operator's page said the bettor holds. Replaces whatever was
       there, including with null: an endpoint that stops answering means the
       balance is unknown, and a stale number is worse than none when it is
       used to tell somebody what they can afford. */
    case 'ACCOUNT':
      return { ...state, account: event.account };

    case 'PLACING':
      return { ...state, phase: 'PLACING', placement: null, error: null };

    case 'PLACEMENT':
      // A refusal goes back to READY with the operator's reason on screen and
      // the slip still there: "there wasn't enough at that moment" is answered
      // by naming a smaller amount, and taking the bet away would make them
      // describe it again from scratch.
      return event.result.status === 'PLACED'
        ? {
            ...state,
            phase: 'PLACED',
            slips: [],
            alternatives: {},
            buildOpen: false,
            buildTyping: false,
            buildPicked: [],
            movedFrom: null,
            presentation: null,
            notPriceable: null,
            mergedLegs: false,
            stake: null,
            stakeAmount: null,
            placement: event.result,
          }
        : { ...state, phase: 'READY', placement: event.result };

    /**
     * Narrow the answer to the one they picked, and nothing else.
     *
     * The other suggestions are dropped rather than kept and greyed: they were
     * alternatives to a question already answered, and leaving them on screen
     * under a stake field asks the bettor to work out which of the four things
     * in front of them the amount belongs to.
     *
     * No request goes out. The price is confirmed when the bet is placed, which
     * is the same order the single-slip path already uses — confirming here
     * would sign a slip against an amount nobody has typed yet.
     */
    case 'SLIP_CHOSEN': {
      const chosen = state.slips.find((slip) => slip.id === event.slipId);
      if (chosen === undefined) return state;

      /**
       * The answer goes into the transcript, because it *is* one.
       *
       * The history handed to the model is this message list, and a choice made
       * by pressing a button used to leave no trace in it. So the next sentence
       * arrived after a question the transcript showed as unanswered, and the
       * model asked it again — correctly, on the record it was given. Live:
       * "Atlanta United to win", answered by tapping a fixture, then "Seattle
       * Sounders to win" came back as "Which Atlanta match did you mean?
       * Seattle is still waiting", with the slip gone from the screen.
       *
       * The text is the label the bettor actually pressed, passed in by the
       * click rather than recomputed here — what is written down should be what
       * they saw, and the view is the only thing that knows how those labels
       * were distinguished from each other.
       */
      const answered =
        event.label === undefined
          ? state.messages
          : [
              ...state.messages,
              {
                from: 'player' as const,
                text: event.label,
                ...(event.at === undefined ? {} : { at: event.at }),
              },
            ];

      /**
       * The question is answered, so the panel must stop asking it.
       *
       * `slipTitle` is chosen from `presentation`, and this left it on
       * `CHOICES` — so a finished accumulator sat under the heading "Which did
       * you mean?" with nothing left to choose between. Seen by using the
       * demo rather than by any test: every assertion here was about the slips
       * and none about the words above them.
       *
       * Null rather than a shape of its own: the heading falls through to
       * "Your selections", and the only other reader of this field returns
       * nothing for `CHOICES` and nothing for null alike.
       */
      return {
        ...state,
        slips: [chosen],
        messages: answered,
        presentation: null,
        error: null,
      };
    }

    case 'BUILD_TOGGLED':
      // Closing forgets the ticks. Reopening to find yesterday's selection
      // still ticked is a slip nobody built.
      return state.buildOpen
        ? { ...state, buildOpen: false, buildPicked: [] }
        : { ...state, buildOpen: true };

    case 'BUILD_TYPING':
      return { ...state, buildTyping: event.open };

    case 'BUILD_PICKED':
      return {
        ...state,
        buildPicked: state.buildPicked.includes(event.ref)
          ? state.buildPicked.filter((ref) => ref !== event.ref)
          : [...state.buildPicked, event.ref],
      };

    case 'SLIP_EXTENDED':
      /**
       * The bigger slip replaces the smaller one, and the amount is cleared.
       *
       * Cleared rather than kept, because the price just changed: an amount
       * chosen against 1.93 was chosen against a payout that no longer exists,
       * and carrying it forward would show a figure nobody agreed to. They pick
       * again, which is one tap on a suggestion.
       */
      return {
        ...state,
        slips: [event.slip],
        // The extend route answers about one slip, so this replaces the map
        // rather than merging into it: the others were readings of a question
        // that has been decided, and their refs name selections nobody can now
        // reach from this screen.
        alternatives: { [event.slip.id]: event.alternatives },
        // Closed again: the thing that was being decided has been decided.
        buildOpen: false,
        buildTyping: false,
        buildPicked: [],
        stakeAmount: null,
        error: null,
      };

    /**
     * The same repricing as SLIP_EXTENDED, and one deliberate difference.
     *
     * The amount stays. On an extend it is cleared, because the price went up
     * and an amount chosen against the old one was chosen against a payout that
     * no longer exists — the bettor should look again before committing more.
     * Taking a leg *off* is the opposite move: they are narrowing a bet they
     * have already sized, and making them name the figure again is exactly the
     * "redo the whole thing" this control exists to avoid. The payout on screen
     * is recomputed from the new price, so nothing stale is shown.
     */
    case 'SLIP_REDUCED':
      return {
        ...state,
        slips: [event.slip],
        alternatives: { [event.slip.id]: event.alternatives },
        // Closed again: what was being decided has been decided.
        buildOpen: false,
        buildTyping: false,
        buildPicked: [],
        error: null,
      };

    case 'CONFIRM':
      return { ...state, phase: 'CONFIRMING', error: null };

    case 'CONFIRMED':
      // The presentation goes too. HANDED_OFF is what the bettor is told now,
      // and leaving the old one set would print "offered as separate bets"
      // underneath a bet that has already gone to the operator's slip.
      return {
        ...state,
        phase: 'HANDED_OFF',
        slips: [],
        alternatives: {},
        buildOpen: false,
        buildTyping: false,
        buildPicked: [],
        movedFrom: null,
        presentation: null,
        notPriceable: null,
        stake: null,
        stakeAmount: null,
      };

    case 'PRICE_MOVED':
      // Back to READY with the new price on screen. The bettor has to agree
      // again — including when the move is in their favour, because accepting
      // silently means confirming a bet nobody agreed to.
      //
      // The amount survives, because they did agree to that part. What they
      // have not agreed to is the price, and the card cannot be confirmed
      // again until they do — the payout on screen is recomputed from the new
      // number, so what they are agreeing to is what they would get.
      return {
        ...state,
        phase: 'READY',
        slips: [event.slip],
        movedFrom: event.previousTotalPrice,
      };

    case 'FAILED':
      return { ...state, phase: 'READY', error: event.kind };
  }
}
